import React, { useState } from 'react';
import axios from 'axios';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { Heart, DollarSign, Plus, Target, Repeat } from 'lucide-react';
import SetupModal from './sqs/SetupModal';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DONATION_TYPES = [
  {
    id: 'one-time', icon: Heart, title: 'One-time donation',
    desc: 'Accept simple one-time donations with suggested amounts.',
    fields: [
      { key: 'campaign_name', label: 'Campaign name', placeholder: 'Help Us Grow', required: true },
      { key: 'description', label: 'Why are you raising funds?', type: 'textarea', required: true },
      { key: 'amounts', label: 'Suggested amounts ($)', placeholder: '25, 50, 100, 250', required: true },
      { key: 'paypal_email', label: 'PayPal email (optional)', type: 'email' },
    ],
  },
  {
    id: 'monthly', icon: Repeat, title: 'Monthly giving',
    desc: 'Recurring monthly donations from your supporters.',
    fields: [
      { key: 'campaign_name', label: 'Program name', placeholder: 'Sustaining Members', required: true },
      { key: 'tiers', label: 'Monthly tiers ($)', placeholder: '5, 15, 30, 50', required: true },
      { key: 'perks', label: 'Member perks', type: 'textarea', placeholder: 'What do monthly supporters receive?' },
    ],
  },
  {
    id: 'goal', icon: Target, title: 'Goal-based campaign',
    desc: 'Show a progress bar that fills as donations come in.',
    fields: [
      { key: 'campaign_name', label: 'Campaign name', placeholder: 'New Studio Fund', required: true },
      { key: 'goal_amount', label: 'Goal amount ($)', type: 'number', placeholder: '10000', required: true },
      { key: 'deadline', label: 'Deadline', type: 'date' },
      { key: 'description', label: 'Story', type: 'textarea', required: true },
    ],
  },
];

export default function DashboardDonations() {
  const [active, setActive] = useState(null);
  const token = localStorage.getItem('token');

  const handleSubmit = async (typeId, values) => {
    try {
      await axios.post(`${API}/builder/donations/campaigns`, { type: typeId, ...values }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      const all = JSON.parse(localStorage.getItem('wb_donations') || '[]');
      all.push({ type: typeId, ...values, created_at: new Date().toISOString() });
      localStorage.setItem('wb_donations', JSON.stringify(all));
    }
    toast.success('Donation campaign created');
  };

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Donations"
        title="Donations"
        subtitle="Accept one-time, monthly, and goal-based donations from your supporters."
        actions={<SqsButton variant="primary" size="sm" icon={Plus} onClick={() => setActive(DONATION_TYPES[0])} data-testid="donations-new">New Campaign</SqsButton>}
      />
      <div className="p-10 max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-5">
        {DONATION_TYPES.map(t => (
          <div key={t.id} className="bg-[#15152E] border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.16] transition-all flex flex-col" data-testid={`donation-type-${t.id}`}>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EC4899]/20 to-[#8B5CF6]/20 flex items-center justify-center mb-4">
              <t.icon size={22} className="text-[#A78BFA]" />
            </div>
            <h3 className="text-[15px] font-bold text-white mb-2">{t.title}</h3>
            <p className="text-[12.5px] text-[#9CA3AF] leading-relaxed mb-5 flex-1">{t.desc}</p>
            <button
              onClick={() => setActive(t)}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_0_24px_rgba(139,92,246,0.4)] transition-all"
              data-testid={`donation-setup-${t.id}`}
            >
              Set up
            </button>
          </div>
        ))}
      </div>

      {active && (
        <SetupModal
          open={!!active}
          onClose={() => setActive(null)}
          title={`Set up · ${active.title}`}
          icon={active.icon}
          fields={active.fields}
          submitLabel="Create campaign"
          onSubmit={(v) => handleSubmit(active.id, v)}
        />
      )}
    </>
  );
}
